import React, { useState, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  User, Mail, Lock, Phone, MapPin, Store, FileText,
  CheckCircle, Eye, EyeOff, ArrowLeft, Crosshair,
  Upload, X, AlertCircle, Navigation, Gift
} from 'lucide-react';
import API from '../utils/api';
import { toast } from 'react-toastify';
import OtpVerification from '../components/OtpVerification';
import BrandLogo from '../components/BrandLogo';

const STEPS = ['Personal', 'Shop Details', 'Location', 'Documents'];
const MAX_MB = 10;

// ── File Upload Box ──────────────────────────────────────────────────────
function FileUploadBox({ label, hint, value, onChange, onClear }) {
  const ref = useRef();
  const [drag, setDrag] = useState(false);
  const [err, setErr] = useState('');

  const process = (file) => {
    setErr('');
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['png','jpg','jpeg','pdf'].includes(ext)) { setErr('Only PNG, JPG, JPEG or PDF allowed'); return; }
    if (file.size > MAX_MB * 1024 * 1024) { setErr(`File too large. Max ${MAX_MB}MB allowed`); return; }
    const reader = new FileReader();
    reader.onload = (e) => onChange({ name: file.name, type: file.type, size: file.size, base64: e.target.result });
    reader.readAsDataURL(file);
  };

  const onDrop = (e) => { e.preventDefault(); setDrag(false); process(e.dataTransfer.files[0]); };
  const isPDF = value?.name?.toLowerCase().endsWith('.pdf');

  return (
    <div className="owner-upload-box" style={{ marginBottom: 16 }}>
      <label className="form-label">{label}</label>
      <div style={{ fontSize: 11, color: 'var(--gray)', marginBottom: 6 }}>{hint}</div>

      {value ? (
        <div style={{ border: '2px solid var(--green)', borderRadius: 12, overflow: 'hidden', position: 'relative' }}>
          {!isPDF
            ? <img className="owner-upload-preview" src={value.base64} alt={label} style={{ width: '100%', maxHeight: 160, objectFit: 'cover', display: 'block' }} />
            : <div style={{ background: '#FEF2F2', padding: 20, textAlign: 'center' }}>
                <FileText size={32} color="var(--red)" style={{ marginBottom: 6 }} />
                <div style={{ fontWeight: 600, fontSize: 13 }}>{value.name}</div>
              </div>
          }
          <div style={{ background: 'rgba(0,0,0,0.6)', position: 'absolute', bottom: 0, left: 0, right: 0, padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ color: 'white', fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>{value.name}</div>
              <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11 }}>
                {value.size < 1024*1024 ? `${Math.round(value.size/1024)}KB` : `${(value.size/1024/1024).toFixed(1)}MB`}
              </div>
            </div>
            <button onClick={onClear} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 6, padding: '5px 10px', color: 'white', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
              <X size={12} /> Remove
            </button>
          </div>
          <div style={{ position: 'absolute', top: 8, right: 8, background: 'var(--green)', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CheckCircle size={14} color="white" />
          </div>
        </div>
      ) : (
        <div
          onDragOver={e => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          onClick={() => ref.current.click()}
          className="owner-upload-drop"
          style={{ border: `2px dashed ${drag ? 'var(--orange)' : err ? 'var(--red)' : 'var(--gray-border)'}`, borderRadius: 12, padding: '24px 16px', textAlign: 'center', cursor: 'pointer', background: drag ? 'var(--orange-soft)' : 'var(--gray-light)', transition: 'all 0.2s' }}>
          <div className="owner-upload-icon" style={{ width: 40, height: 40, background: 'white', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px', boxShadow: 'var(--shadow)' }}>
            <Upload size={18} color="var(--orange)" />
          </div>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 3 }}>Drop file or <span style={{ color: 'var(--orange)' }}>browse</span></div>
          <div style={{ color: 'var(--gray)', fontSize: 11 }}>PNG, JPG, JPEG or PDF · Max {MAX_MB}MB</div>
        </div>
      )}
      {err && <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: 'var(--red)', fontSize: 12, marginTop: 5 }}><AlertCircle size={12} /> {err}</div>}
      <input ref={ref} type="file" accept=".png,.jpg,.jpeg,.pdf" style={{ display: 'none' }} onChange={e => process(e.target.files[0])} />
    </div>
  );
}

// ── GPS Detector ─────────────────────────────────────────────────────────
function GPSDetector({ onDetect }) {
  const [status, setStatus] = useState('idle');
  const [errMsg, setErrMsg] = useState('');

  const detect = () => {
    if (!navigator.geolocation) { setErrMsg('GPS not supported. Enter manually.'); setStatus('error'); return; }
    setStatus('loading');
    setErrMsg('');
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng, accuracy } = pos.coords;
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`);
          const data = await res.json();
          const a = data.address || {};
          onDetect({
            lat: lat.toFixed(6), lng: lng.toFixed(6),
            address: [a.road, a.neighbourhood, a.suburb].filter(Boolean).join(', '),
            city: a.city || a.town || a.village || a.county || '',
            state: a.state || '',
            pincode: a.postcode || '',
          });
          setStatus('success');
          toast.success('Location detected & filled!', { autoClose: 2000 });
        } catch {
          onDetect({ lat: lat.toFixed(6), lng: lng.toFixed(6), address: '', city: '', state: '', pincode: '' });
          setStatus('success');
          toast.info('GPS coordinates set. Please fill city & state.', { autoClose: 3000 });
        }
      },
      (err) => {
        setStatus('error');
        const msgs = { 1: 'Permission denied. Allow location or enter manually.', 2: 'Location unavailable. Enter manually.', 3: 'Timeout. Enter manually.' };
        setErrMsg(msgs[err.code] || 'GPS error.');
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
    );
  };

  if (status === 'idle') return (
    <button type="button" onClick={detect}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: 'linear-gradient(135deg,#1A73E8,#0D62C9)', color: 'white', border: 'none', borderRadius: 10, padding: '12px', cursor: 'pointer', fontWeight: 600, fontSize: 14, fontFamily: 'var(--font-body)', width: '100%', marginBottom: 14 }}>
      <Crosshair size={16} /> Detect Shop Location via GPS
    </button>
  );

  if (status === 'loading') return (
    <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 10, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
      <div className="spinner" style={{ width: 20, height: 20, borderWidth: 2, flexShrink: 0 }} />
      <div style={{ fontSize: 13, color: '#1D4ED8', fontWeight: 600 }}>Detecting location... Allow access if prompted</div>
    </div>
  );

  if (status === 'error') return (
    <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
      <div style={{ display: 'flex', gap: 7, marginBottom: 8, color: 'var(--red)', fontSize: 13 }}>
        <AlertCircle size={14} style={{ flexShrink: 0 }} /> {errMsg}
      </div>
      <button type="button" onClick={detect}
        style={{ background: 'none', border: 'none', color: 'var(--orange)', fontWeight: 600, fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}>
        Try Again
      </button>
    </div>
  );

  return (
    <div style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 10, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
      <CheckCircle size={15} color="var(--green)" />
      <span style={{ fontSize: 13, color: 'var(--green)', fontWeight: 600 }}>Location detected! Verify fields below.</span>
      <button type="button" onClick={() => setStatus('idle')} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray)', display: 'flex' }}><X size={14} /></button>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────
export default function OwnerRegister() {
  const [step, setStep] = useState(0);
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [otpEmail, setOtpEmail] = useState('');
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    name: '', email: '', phone: '', password: '', confirmPw: '',
    shopName: '', description: '',
    address: '', city: '', state: '', pincode: '', lat: '', lng: '',
    referralCode: searchParams.get('ref') || '',
  });
  const [aadharFile, setAadharFile] = useState(null);
  const [workFile, setWorkFile] = useState(null);
  const navigate = useNavigate();

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const onGPS = (data) => setForm(f => ({
    ...f,
    lat: data.lat || f.lat, lng: data.lng || f.lng,
    address: data.address || f.address, city: data.city || f.city,
    state: data.state || f.state, pincode: data.pincode || f.pincode,
  }));

  const validate = () => {
    setError('');
    if (step === 0) {
      if (!form.name.trim()) return setError('Full name is required'), false;
      if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) return setError('Valid email required'), false;
      if (!form.phone.trim() || form.phone.replace(/\D/g,'').length < 10) return setError('Valid 10-digit phone required'), false;
      if (form.password.length < 6) return setError('Password must be at least 6 characters'), false;
      if (form.password !== form.confirmPw) return setError('Passwords do not match'), false;
    }
    if (step === 1 && !form.shopName.trim()) return setError('Shop name is required'), false;
    if (step === 2) {
      if (!form.city.trim() || !form.state.trim()) return setError('City and State are required'), false;
      if (!form.pincode.trim()) return setError('Pincode is required'), false;
    }
    return true;
  };

  const next = () => { if (validate()) setStep(s => s + 1); };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true); setError('');
    try {
      const res = await API.post('/auth/register/owner', {
        name: form.name, email: form.email, phone: form.phone, password: form.password,
        shopName: form.shopName, description: form.description,
        location: {
          address: form.address, city: form.city, state: form.state,
          pincode: form.pincode, lat: parseFloat(form.lat) || 0, lng: parseFloat(form.lng) || 0,
        },
        // Pass documents directly in registration payload
        documents: (aadharFile || workFile) ? {
          aadharCard: aadharFile?.base64 || '',
          aadharFileName: aadharFile?.name || '',
          workingPlaceProof: workFile?.base64 || '',
          workplaceFileName: workFile?.name || '',
        } : null,
        referralCode: form.referralCode,
      });
      setOtpEmail(res.data.email || form.email);
      toast.success('Shop account created. OTP sent to your email.');
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.response?.data?.message || 'Registration failed. Please check all fields and try again.');
    } finally { setLoading(false); }
  };

  // ── Success Screen
  if (done) return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg,#1A1A1A,#2D2D2D)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'white', borderRadius: 20, padding: 44, textAlign: 'center', maxWidth: 400, width: '100%' }} className="fade-in">
        <div style={{ width: 80, height: 80, background: '#ECFDF5', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px' }}>
          <CheckCircle size={48} color="var(--green)" />
        </div>
        <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Shop Registered! 🎉</h2>
        <p style={{ color: 'var(--gray)', fontSize: 14, lineHeight: 1.7, marginBottom: 20 }}>
          Your shop <strong>{form.shopName}</strong> has been registered successfully.<br />
          Admin will review your documents and approve your shop within <strong>24 hours</strong>.
        </p>
        <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10, padding: '12px 14px', textAlign: 'left', marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#92400E', marginBottom: 6 }}>What happens next?</div>
          {['Admin reviews your Aadhar & shop documents','Your shop gets verified & listed publicly','Customers near you can find & order materials','You receive orders on your dashboard'].map((t, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, fontSize: 12, color: '#92400E', marginBottom: 4 }}>
              <span style={{ fontWeight: 700 }}>{i+1}.</span> {t}
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: 'var(--gray)', fontSize: 13 }}>
          <div className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> Redirecting to dashboard...
        </div>
      </div>
    </div>
  );

  return (
    <div className="auth-page auth-owner-page" style={{ minHeight: '100vh', background: 'linear-gradient(135deg,#1A1A1A,#2D2D2D)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', padding: '20px 16px', paddingBottom: 40 }}>

      {/* Back button */}
      <div className="auth-back" style={{ width: '100%', maxWidth: 560, marginBottom: 12 }}>
        <button onClick={() => step === 0 ? navigate('/') : setStep(s => s - 1)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 8, padding: '7px 14px', color: '#9CA3AF', fontSize: 13, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
          <ArrowLeft size={14} /> {step === 0 ? 'Back to Home' : 'Back'}
        </button>
      </div>

      <div style={{ background: 'white', borderRadius: 20, padding: '28px 24px', width: '100%', maxWidth: 560, boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }} className="fade-in auth-card auth-owner-card">
        {otpEmail ? (
          <OtpVerification
            email={otpEmail}
            title="Verify Shop Account"
            subtitle="We sent a 6 digit OTP to your email. Verify to open your owner dashboard."
            onVerified={() => navigate('/owner/dashboard')}
            onBack={() => setOtpEmail('')}
          />
        ) : (
        <>

        {/* Header */}
        <div className="auth-header" style={{ textAlign: 'center', marginBottom: 24 }}>
          <BrandLogo size={52} radius={14} />
          <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 3 }}>Register Your Shop</h1>
          <p style={{ color: 'var(--gray)', fontSize: 13 }}>Step {step + 1} of {STEPS.length} — {STEPS[step]}</p>
        </div>

        {/* Stepper */}
        <div className="auth-stepper" style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 24 }}>
          {STEPS.map((s, i) => (
            <React.Fragment key={i}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, transition: 'all 0.3s', background: i < step ? 'var(--green)' : i === step ? 'var(--orange)' : 'var(--gray-light)', color: i <= step ? 'white' : 'var(--gray)' }}>
                  {i < step ? <CheckCircle size={14} /> : i + 1}
                </div>
                <div style={{ fontSize: 9, color: i === step ? 'var(--orange)' : 'var(--gray)', marginTop: 3, textAlign: 'center', fontWeight: i === step ? 700 : 400 }}>{s}</div>
              </div>
              {i < STEPS.length - 1 && (
                <div style={{ flex: 1, height: 2, background: i < step ? 'var(--green)' : 'var(--gray-border)', margin: '13px 4px 0', transition: 'background 0.3s' }} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Error */}
        {error && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: '#FEF2F2', border: '1px solid #FECACA', color: 'var(--red)', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>
            <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} /> {error}
          </div>
        )}

        {/* ── STEP 0: Personal ── */}
        {step === 0 && (
          <div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Full Name *</label>
                <div style={{ position: 'relative' }}>
                  <User size={14} color="var(--gray)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                  <input className="form-input" style={{ paddingLeft: 38 }} placeholder="Your full name" value={form.name} onChange={e => set('name', e.target.value)} />
                </div>
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Phone *</label>
                <div style={{ position: 'relative' }}>
                  <Phone size={14} color="var(--gray)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                  <input className="form-input" style={{ paddingLeft: 38 }} placeholder="10-digit number" value={form.phone} onChange={e => set('phone', e.target.value)} />
                </div>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Email Address *</label>
              <div style={{ position: 'relative' }}>
                <Mail size={14} color="var(--gray)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                <input className="form-input" style={{ paddingLeft: 38 }} type="email" placeholder="you@example.com" value={form.email} onChange={e => set('email', e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Password *</label>
                <div style={{ position: 'relative' }}>
                  <Lock size={14} color="var(--gray)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                  <input className="form-input" style={{ paddingLeft: 38, paddingRight: 38 }} type={showPw ? 'text' : 'password'} placeholder="Min 6 chars" value={form.password} onChange={e => set('password', e.target.value)} />
                  <button type="button" onClick={() => setShowPw(!showPw)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray)', display: 'flex' }}>
                    {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Confirm Password *</label>
                <div style={{ position: 'relative' }}>
                  <Lock size={14} color="var(--gray)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                  <input className="form-input" style={{ paddingLeft: 38 }} type="password" placeholder="Repeat password" value={form.confirmPw} onChange={e => set('confirmPw', e.target.value)} />
                </div>
              </div>
            </div>
            <div style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 10, padding: '12px 14px' }}>
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <Gift size={13} color="var(--green)" /> Referral Code
              </label>
              <input className="form-input" placeholder="Optional referral code" value={form.referralCode} onChange={e => set('referralCode', e.target.value.toUpperCase())} />
              <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 6 }}>
                Use a friend's code so their supplier referral reward is credited.
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 1: Shop Details ── */}
        {step === 1 && (
          <div>
            <div className="form-group">
              <label className="form-label">Shop Name *</label>
              <div style={{ position: 'relative' }}>
                <Store size={14} color="var(--gray)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                <input className="form-input" style={{ paddingLeft: 38 }} placeholder="e.g. Ravi Building Materials" value={form.shopName} onChange={e => set('shopName', e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Shop Description</label>
              <textarea className="form-input" rows={4} placeholder="Describe what materials you sell, specialties, experience, bulk order info..."
                value={form.description} onChange={e => set('description', e.target.value)} style={{ resize: 'vertical' }} />
            </div>
            <div style={{ background: 'var(--orange-soft)', borderRadius: 10, padding: 12, fontSize: 12, color: 'var(--orange-dark)', lineHeight: 1.6 }}>
              💡 A good description helps customers find you. Mention: materials stocked, bulk discounts, delivery areas, years of experience.
            </div>
          </div>
        )}

        {/* ── STEP 2: Location ── */}
        {step === 2 && (
          <div>
            <GPSDetector onDetect={onGPS} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, color: 'var(--gray)', fontSize: 12 }}>
              <div style={{ flex: 1, height: 1, background: 'var(--gray-border)' }} />
              or enter manually
              <div style={{ flex: 1, height: 1, background: 'var(--gray-border)' }} />
            </div>
            <div className="form-group">
              <label className="form-label">Street / Area *</label>
              <div style={{ position: 'relative' }}>
                <MapPin size={14} color="var(--gray)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                <input className="form-input" style={{ paddingLeft: 38 }} placeholder="Street, Area, Landmark" value={form.address} onChange={e => set('address', e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <div className="form-group" style={{ flex: '2 1 120px' }}>
                <label className="form-label">City *</label>
                <input className="form-input" placeholder="City" value={form.city} onChange={e => set('city', e.target.value)} />
              </div>
              <div className="form-group" style={{ flex: '2 1 100px' }}>
                <label className="form-label">State *</label>
                <input className="form-input" placeholder="State" value={form.state} onChange={e => set('state', e.target.value)} />
              </div>
              <div className="form-group" style={{ flex: '1 1 80px' }}>
                <label className="form-label">Pincode *</label>
                <input className="form-input" placeholder="Pincode" value={form.pincode} onChange={e => set('pincode', e.target.value)} />
              </div>
            </div>
            <div style={{ background: 'var(--gray-light)', borderRadius: 10, padding: '12px 14px' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
                <Navigation size={12} /> GPS Coordinates {form.lat && form.lng ? <span style={{ color: 'var(--green)' }}>✓ Set ({Number(form.lat).toFixed(4)}, {Number(form.lng).toFixed(4)})</span> : <span style={{ fontWeight: 400 }}>(optional)</span>}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input className="form-input" placeholder="Latitude e.g. 17.3850" value={form.lat} onChange={e => set('lat', e.target.value)} />
                <input className="form-input" placeholder="Longitude e.g. 78.4867" value={form.lng} onChange={e => set('lng', e.target.value)} />
              </div>
              <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 5 }}>
                📌 From <a href="https://maps.google.com" target="_blank" rel="noreferrer" style={{ color: 'var(--orange)' }}>Google Maps</a>: right-click your shop → copy coordinates
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 3: Documents ── */}
        {step === 3 && (
          <div>
            <div className="owner-doc-notice" style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10, padding: '12px 14px', marginBottom: 16, fontSize: 12, color: '#92400E', lineHeight: 1.7 }}>
              <strong>📋 Document Verification</strong><br />
              Upload clear photos or scanned PDFs. PNG, JPG, JPEG, PDF · Max 10MB each.<br />
              Admin verifies within 24 hours before your shop goes live. You can also upload later from your dashboard.
            </div>
            <div className="owner-doc-grid">
            <FileUploadBox
              label="Aadhar Card"
              hint="Front side of Aadhar card — PNG, JPG or PDF, max 10MB"
              value={aadharFile} onChange={setAadharFile} onClear={() => setAadharFile(null)}
            />
            <FileUploadBox
              label="Shop / Working Place Proof"
              hint="Shop license, rental agreement, electricity bill or property document — PNG, JPG or PDF, max 10MB"
              value={workFile} onChange={setWorkFile} onClear={() => setWorkFile(null)}
            />
            </div>
            {(!aadharFile || !workFile) && (
              <div className="owner-doc-optional" style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#1D4ED8' }}>
                <AlertCircle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                Documents are optional now — you can upload them later from your dashboard. But uploading now speeds up approval.
              </div>
            )}
          </div>
        )}

        {/* Navigation buttons */}
        <div className="owner-register-actions" style={{ display: 'flex', gap: 10, marginTop: 22 }}>
          {step > 0 && (
            <button type="button" onClick={() => { setError(''); setStep(s => s - 1); }}
              className="btn-outline" style={{ flex: 1, justifyContent: 'center', padding: '12px' }}>
              ← Back
            </button>
          )}
          {step < STEPS.length - 1 ? (
            <button type="button" onClick={next}
              className="btn-primary" style={{ flex: 2, justifyContent: 'center', padding: '13px', fontSize: 15 }}>
              Next →
            </button>
          ) : (
            <button type="button" onClick={handleSubmit}
              className="btn-primary" style={{ flex: 2, justifyContent: 'center', padding: '13px', fontSize: 15 }}
              disabled={loading}>
              {loading ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div className="spinner" style={{ width: 16, height: 16, borderWidth: 2, borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'white' }} />
                  Registering...
                </span>
              ) : '🎉 Complete Registration'}
            </button>
          )}
        </div>

        <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--gray)', marginTop: 16 }}>
          Already registered? <Link to="/login" style={{ color: 'var(--orange)', fontWeight: 600 }}>Sign in</Link>
        </p>
        </>
        )}
      </div>
    </div>
  );
}
